import type { VendorConnectionData } from '@/apis/vendorConnections';
import { tagStatus } from './vendorFormat';

// The real Default team is created black (backend team_services), and the rest of
// the app shows its dot in that colour.
const DEFAULT_TEAM_COLOR = '#000000';

/** A connected vendor shaped like a row of the Transport providers table, so it
 * lists beside the providers with the same columns and edit/delete icons. The
 * original connection rides along for the row actions. */
export interface VendorTableRow {
  key: string;
  id: string;
  courier_name: string;
  description: string;
  status: string;
  status_detail: string;
  teams: Array<{ name: string; color: string }>;
  contact: Array<{
    name: string;
    email: string | null;
    is_primary_contact: boolean;
  }>;
  permissions: { is_update: boolean; is_delete: boolean };
  vendor_connection: VendorConnectionData;
}

export const toVendorRow = (vendor: VendorConnectionData): VendorTableRow => ({
  key: `vendor:${vendor.vendor}`,
  id: `vendor:${vendor.vendor}`,
  courier_name: vendor.display_name,
  description: vendor.description,
  status: tagStatus(vendor.status),
  status_detail: vendor.status_detail,
  // vendors are company-wide for now, so every row is on the default team
  teams: [{ name: 'Default', color: DEFAULT_TEAM_COLOR }],
  contact: vendor.connected_by
    ? [
        {
          name: vendor.connected_by.name,
          email: vendor.connected_by.email,
          is_primary_contact: true,
        },
      ]
    : [],
  permissions: { is_update: true, is_delete: true },
  vendor_connection: vendor,
});

/** Rows for the vendors this company has connected, narrowed by the table's
 * search text. Vendors that are not connected are only offered in the Add drawer. */
export const vendorRowsFor = (
  vendors: VendorConnectionData[],
  search: string
): VendorTableRow[] => {
  const query = search.trim().toLowerCase();
  return vendors
    .filter(
      (vendor) =>
        vendor.connected &&
        (!query || vendor.display_name.toLowerCase().includes(query))
    )
    .map(toVendorRow);
};
